/**
 * A sheet's cell flush after an ephemeral→server promote: the cells an
 * ephemeral sheet holds locally (its hydrate seeds and pre-promote edits),
 * tracked as they are written, and the flush that persists them once the
 * promote landed — the promote payload carries rows, not cells.
 *
 * **Package-internal**: `Sheet` owns one (`sheetInternals(sheet)
 * .trackPromotionFlushCell` / `.flushCellsToBackend`). The sheet's
 * ephemeral flag, its canonical and local cell stores, its rows and its
 * backend come through `SheetPromotionFlush.Host`; it names no row or store
 * type (the sheet instantiates `Row`), so it sits outside the rows' type
 * knot.
 */

import type { Option } from "../core/option.ts";
import type { CellEntry, CellValue } from "../core/dtype.ts";
import type { CellId } from "../ids/ids.ts";
import { RowId } from "../ids/row-id.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

type PromotionFlushCell = {
  readonly rowId: RowLocalId;
  readonly canonicalKey: Option<string>;
  readonly date: NaiveDate.Partial;
  readonly cellId: Option<CellId>;
};

/** See the module note: a sheet's post-promote cell flush, over its `Host`. */
export class SheetPromotionFlush<Row extends { readonly id: RowLocalId }> {
  readonly #host: SheetPromotionFlush.Host<Row>;
  readonly #cells = new Map<string, PromotionFlushCell>();
  /**
   * `true` when the sheet was BORN ephemeral, and so tracked every cell it
   * holds locally (hydrate seeds + pre-promote edits) in `#cells`. Fixed at
   * construction: `markPromoted` flips the sheet's ephemeral flag before the
   * flush runs, and the flush must still know that the tracked set is
   * complete. See `flush`.
   */
  readonly #tracksAll: boolean;

  constructor(host: SheetPromotionFlush.Host<Row>, bornEphemeral: boolean) {
    this.#host = host;
    this.#tracksAll = bornEphemeral;
  }

  /** Track a cell written on an ephemeral sheet (a no-op once the sheet is not ephemeral). */
  track(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): void {
    if (!this.#host.ephemeral()) return;
    const canonicalKey = this.#host.canonicalKeyOf(rowId);
    const ownerKey = canonicalKey ?? rowId.toString();
    this.#cells.set(
      `${ownerKey}|${date.toString()}|${cellId ?? ""}`,
      { rowId, canonicalKey, date, cellId },
    );
  }

  /**
   * Persist every locally-held source cell to the backend. Called after an
   * ephemeral→server promote — the promote payload carries rows, not cells, so
   * the claimed sheet's seed + just-edited cells would otherwise be lost on
   * reload. Skips derived (formula) rows and server-owned read-only provider
   * rows. Writable canonical rows (for example `:~lona:journal`) are flushed
   * from the canonical store using their lookup key, matching `SourceRow.write`.
   */
  async flush(): Promise<void> {
    // A sheet born ephemeral tracked every cell it holds locally — hydrate
    // seeds and pre-promote edits — so the tracked set IS the flush, even
    // when it is empty. Anything else it holds was READ from the server,
    // above all the client-scoped `canonicalCells` store every sheet shares
    // (the fetched `:~weather:{lid}` days, events, stickers). Walking those
    // re-POSTed server data one awaited write at a time inside
    // `_applyPromotion`, and a promote an add-row triggered (no tracked
    // cells) held that add-row behind the whole walk (weather-location-rebind,
    // c7). The all-cells walk below is for sheets that never tracked
    // (hydrated non-ephemeral).
    const host = this.#host;
    if (this.#tracksAll || this.#cells.size > 0) {
      const cells = [...this.#cells.values()];
      this.#cells.clear();
      for (const pending of cells) {
        const canonicalKey = pending.canonicalKey;
        const rowId = canonicalKey != null
          ? RowId.of(canonicalKey)
          : RowId.fromLocalId(pending.rowId);
        const value = canonicalKey != null
          ? host.canonicalCell(
            canonicalKey,
            pending.date,
            pending.cellId,
          )
          : host.localCell(
            pending.rowId,
            pending.date,
            pending.cellId,
          );
        const entry: CellEntry = {
          date: pending.date,
          id: pending.cellId ?? null,
          value: value ?? null,
          style: null,
        };
        try {
          await host.writeCell(rowId, entry);
        } catch (err) {
          Tracing.log("cell-flush/failed", {
            row: rowId.toString(),
            error: String(err),
          });
        }
      }
      return;
    }

    for (const row of host.rows()) {
      if (!host.writable(row)) continue;

      const canonicalKey = host.canonicalKeyOf(row.id);
      const rowId = canonicalKey !== null
        ? RowId.of(canonicalKey)
        : RowId.fromLocalId(row.id);
      const cells = canonicalKey !== null
        ? host.canonicalCells(canonicalKey)
        : host.localCells(row.id);
      for (const entry of cells) {
        try {
          await host.writeCell(rowId, entry);
        } catch (err) {
          // Best-effort: the claim (rows) already landed; a single cell write
          // failing must not undo it. Log and continue.
          Tracing.log("cell-flush/failed", {
            row: rowId.toString(),
            error: String(err),
          });
        }
      }
    }
  }
}

/** Types that go with `SheetPromotionFlush`: `Host`. */
export namespace SheetPromotionFlush {
  /** What the flush reads and writes through its sheet. */
  export interface Host<Row> {
    /** Whether the sheet is ephemeral now (no server record yet). */
    ephemeral(): boolean;
    /** Row `id`'s lookup key when it is a canonical (`:~`) key, else `null`. */
    canonicalKeyOf(id: RowLocalId): Option<string>;
    /** One canonical cell's value (the client-scoped store). */
    canonicalCell(key: string, date: NaiveDate.Partial, cellId: Option<CellId>): Option<CellValue>;
    /** One sheet-local cell's value. */
    localCell(id: RowLocalId, date: NaiveDate.Partial, cellId: Option<CellId>): Option<CellValue>;
    /** Every cell of canonical key `key`. */
    canonicalCells(key: string): readonly CellEntry[];
    /** Every sheet-local cell of row `id`. */
    localCells(id: RowLocalId): readonly CellEntry[];
    /** Every row of the sheet, breadth-first from the root (`Sheet.allRows`). */
    rows(): readonly Row[];
    /** Whether `row` holds cells the flush persists (a writable, non-formula source row), read as the walk reaches it. */
    writable(row: Row): boolean;
    /** Write one cell through the backend. */
    writeCell(rowId: RowId, entry: CellEntry): Promise<void>;
  }
}
